
import React, { useEffect, useState, useRef } from 'react'

import { TextareaAutosize } from '@mui/base/TextareaAutosize';

import clsx from "clsx"
import countWords from '../../countWords';

interface TextAreaProps {
    minRows?: number;
    maxRows?: number;
    maxLength?: number;
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    color?: string;
    className?: string;
    value?: string;
    onChange?: (value: string) => void;
    styled?: boolean;
    isDisabled?: boolean;
    autoFocus?: boolean;
    [key: string]: any;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
    const {
        minRows = 2, 
        maxRows,
        maxLength,
        onFocus, 
        onBlur, 
        color, 
        className,
        value: givenValue, 
        onChange = () => {},
        styled = false,
        isDisabled = false,

        initialValue,

        ...otherProps
    } = props;

    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const [focused, setFocused] = useState(false);

    const value = givenValue || initialValue;

    // 使用 countWords 函数获取当前字数
    const currentWordCount = countWords(value || "");
    const isOverLimit = maxLength ? currentWordCount > maxLength : false;

    useEffect(() => {
        if (props.autoFocus && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 1);
        }
    }, [inputRef.current, props.autoFocus]);

    // Base classes for the container
    const containerClasses = clsx(
        // Base styles equivalent to .lc-text-area
        "rounded-sm flex flex-col p-2 relative",
        
        // Styled variant equivalent to .is-styled
        styled && {
            'border': true,
            'border-transparent': !focused,
            // Using CSS custom properties for colors as they're defined in the design system
            'bg-[var(--gray-2)]': true,
            'border-[var(--color-9)]': focused,
            'bg-[var(--lc-color-bg-1)]': focused
        },
        // Disabled state
        isDisabled && "cursor-not-allowed opacity-60",
        
        className
    );

    // Classes for the textarea element
    const textareaClasses = clsx(
        // Base textarea styles
        "flex-grow leading-6 border-none resize-none outline-none bg-inherit",
        
        // // Styled variant textarea background
        // styled && [
        //     "bg-[var(--gray-2)]",
        //     focused && "bg-[var(--lc-color-bg-1)]"
        // ],
        
        // Disabled state
        isDisabled && "cursor-not-allowed",
        // textAreaClassName,
    );

    // 计数器样式
    const counterClasses = clsx(
        "text-xs self-end mt-1",
        {
            'text-gray-500': !isOverLimit,
            'text-red-500': isOverLimit
        }
    );

    return (
        <div 
            className={containerClasses}
            data-focused={focused} 
            data-color={color} 
            data-has-value={value && value.trim().length > 0}
        >
            <TextareaAutosize 
                {...{
                    minRows, 
                    maxRows,
                    value: value || "",
                    className: textareaClasses,
                    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const newValue = e.target.value;
                        
                        // 如果设置了 maxLength，使用 countWords 检查字数限制
                        if (maxLength && countWords(newValue) > maxLength) {
                            return; // 不允许超过最大字数
                        }
                        
                        onChange(newValue);
                    },
                    onFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => {
                        if (onFocus) { onFocus(e); }
                        setFocused(true);
                    },
                    onBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => {
                        if (onBlur) { onBlur(e); }
                        setFocused(false);
                    },
                    ref: (r: HTMLTextAreaElement | null) => {
                        if (ref && typeof ref === 'object' && ref !== null) {
                            (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = r;
                        } else if (typeof ref === 'function') {
                            ref(r);
                        }
                        inputRef.current = r;
                    },
                    disabled: isDisabled,
                    ...otherProps
                }} 
            />
            
            {/* 字数计数器 */}
            {maxLength && (
                <div className={counterClasses}>
                    {currentWordCount}{maxLength ? `/${maxLength}` : ''}
                </div>
            )}
        </div>
    );
});

TextArea.displayName = 'TextArea';

export default TextArea;